<template>
  <form @submit.prevent="handleSubmit">
    <div class="few-remove-grid">
      <div class="few-remove-copy">
        <label class="active teal-text">Few Principal Removal</label>
        <h6>Paste a short list of principal IDs</h6>
        <p>
          Separate principal IDs with commas or line breaks. Each matched
          principal (and their family) will move to the Bin using the same rule as single
          and bulk removal.
        </p>
      </div>

      <div class="few-remove-form">
        <div class="input-field">
          <textarea
            id="few_remove_enrollee_numbers"
            class="native-textarea"
            v-model="enrolleeIds"
          ></textarea>
          <label for="few_remove_enrollee_numbers">Principal Enrollee IDs</label>
        </div>

        <div class="few-preview" v-if="parsedEnrolleeIds.length">
          <span class="chip" v-for="id in parsedEnrolleeIds" :key="id">
            {{ id }}
          </span>
        </div>

        <div v-if="message" class="remove-message success-message">
          <i class="material-icons">check_circle</i>
          <span>{{ message }}</span>
        </div>

        <div v-if="error" class="remove-message error-message">
          <i class="material-icons">error</i>
          <span>{{ error }}</span>
        </div>
      </div>
    </div>

    <div class="remove-submit-strip">
      <button
        class="waves-effect waves-light btn-large red darken-3"
        :class="{ disabled: !canSubmit }"
      >
        <i class="material-icons right">delete_sweep</i>
        {{ isSubmitting ? "Removing" : "Move List to Bin" }}
      </button>
    </div>
  </form>
</template>

<script>
import { ref, computed, nextTick } from "vue";
import postItem from "@/composables/postItem";
import nativeUi from "@/ui/native-ui";

export default {
  name: "EnrolleeRemoveFewOnboardComponent",
  emits: ["complete"],
  setup(props, context) {
    const enrolleeIds = ref("");
    const error = ref(null);
    const message = ref(null);
    const isSubmitting = ref(false);

    const parsedEnrolleeIds = computed(() => {
      return enrolleeIds.value
        .split(/[\n,]+/)
        .map((id) => id.trim())
        .filter(Boolean)
        .filter((id, index, ids) => ids.indexOf(id) === index);
    });

    const canSubmit = computed(() => {
      return parsedEnrolleeIds.value.length && !isSubmitting.value;
    });

    const resetFields = async () => {
      await nextTick();
      nativeUi.textareaAutoResize(document.querySelector("#few_remove_enrollee_numbers"));
      nativeUi.updateTextFields();
    };

    const handleSubmit = async () => {
      if (!canSubmit.value) {
        return;
      }

      isSubmitting.value = true;
      error.value = null;
      message.value = null;

      const { res, err } = await postItem(
        "principal/bulk/remove",
        parsedEnrolleeIds.value
      );

      if (res.value) {
        const updatedRows = res.value.updated_rows || 0;
        const requestedRows = res.value.requested_rows || parsedEnrolleeIds.value.length;

        message.value = `${updatedRows} of ${requestedRows} principal families moved to Bin.`;
        enrolleeIds.value = "";
        context.emit("complete", { status: true, value: updatedRows });
        resetFields();
      } else {
        error.value = err.value || "Few removal failed.";
      }

      isSubmitting.value = false;
    };

    return {
      canSubmit,
      enrolleeIds,
      error,
      handleSubmit,
      isSubmitting,
      message,
      parsedEnrolleeIds,
    };
  },
};
</script>

<style scoped>
.few-remove-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(220px, 0.8fr) minmax(280px, 1.2fr);
}

.few-remove-copy {
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  padding: 16px;
}

.few-remove-copy h6 {
  color: #263238;
  font-size: 1.05rem;
  font-weight: 700;
  margin: 6px 0 8px;
}

.few-remove-copy p {
  color: #607d8b;
  line-height: 1.55;
  margin: 0;
}

.few-remove-form {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  padding: 16px;
}

.few-preview {
  border-top: 1px solid #eeeeee;
  margin-top: 8px;
  padding-top: 12px;
}

.remove-message {
  align-items: center;
  border-radius: 6px;
  display: flex;
  gap: 10px;
  margin-top: 12px;
  padding: 10px 12px;
}

.remove-message i {
  font-size: 20px;
}

.success-message {
  background: #f1f8e9;
  color: #33691e;
}

.error-message {
  background: #ffebee;
  color: #b71c1c;
}

.remove-submit-strip {
  background: #f5f5f5;
  border-radius: 6px;
  margin-top: 18px;
  padding: 18px;
}

@media (max-width: 800px) {
  .few-remove-grid {
    grid-template-columns: 1fr;
  }
}
</style>
